import React, { useState, useEffect } from 'react';
import { uploadData, getUrl, remove, list } from 'aws-amplify/storage';

const folderStructure = [
  '01 Ideas',
  '02 Ready For Band (agreed)',
  '03 Rehearsal Recordings',
  '04 Shed Studio Demos',
  '05 Posh Studio Recordings',
  '06 KEXP Sessions'
];

const FileExplorer = () => {
  const [currentFolder, setCurrentFolder] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (currentFolder) {
      listFiles();
    }
  }, [currentFolder]);

  const listFiles = async () => {
    try {
      const fileList = await list({
        prefix: `${currentFolder}/`
      });
      setFiles(fileList.items.filter(file => file.key !== `${currentFolder}/`));
    } catch (error) {
      console.log('Error listing files: ', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadStatus(`Selected: ${file.name} (${formatFileSize(file.size)})`);
    }
  };

  const startUpload = async () => {
    if (!selectedFile || !currentFolder) {
      setUploadStatus('No file selected or no folder selected');
      return;
    }
    
    // Calculate file size in MB
    const fileSizeMB = selectedFile.size / (1024 * 1024);
    console.log(`File size: ${fileSizeMB.toFixed(2)} MB`);
    setUploadStatus(`Preparing to upload ${fileSizeMB.toFixed(2)} MB file...`);
    
    try {
      setUploading(true);
      setUploadProgress(0);
      
      // Configure upload options
      const uploadOptions = {
        onProgress: (progress) => {
          const percentUploaded = Math.round((progress.loaded / progress.total) * 100);
          setUploadProgress(percentUploaded);
          setUploadStatus(`Uploading: ${percentUploaded}% (${(progress.loaded / (1024 * 1024)).toFixed(2)} MB of ${(progress.total / (1024 * 1024)).toFixed(2)} MB)`);
        },
        contentType: selectedFile.type || 'application/octet-stream'
      };
      
      console.log('Starting upload with options:', uploadOptions);
      
      await uploadData({
        key: `${currentFolder}/${selectedFile.name}`,
        data: selectedFile,
        options: uploadOptions
      }).result;
      
      console.log('Upload completed successfully');
      setUploading(false);
      setUploadStatus('Upload complete!');
      setSelectedFile(null);
      // Reset the file input
      document.getElementById('file-upload').value = '';
      setTimeout(() => setUploadStatus(''), 3000);
      listFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploading(false);
      setUploadStatus(`Upload failed: ${error.message}`);
    }
  };

  const handleDownload = async (key) => {
    try {
      const result = await getUrl({
        key: key,
        options: {
          download: true
        }
      });
      
      // Create a link and trigger download
      const a = document.createElement('a');
      a.href = result.url;
      a.download = key.split('/').pop();
      a.click();
    } catch (error) {
      console.log('Error downloading file: ', error);
    }
  };

  const handleDelete = async (key) => {
    try {
      await remove({ key });
      listFiles();
    } catch (error) {
      console.log('Error deleting file: ', error);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="container">
      <h1>File Explorer</h1>
      
      {!currentFolder ? (
        <div className="folder-grid">
          {folderStructure.map((folder) => (
            <div 
              key={folder} 
              className="folder" 
              onClick={() => setCurrentFolder(folder)}
            >
              <h3 className="folder-title">{folder}</h3>
              <p>Click to open</p>
            </div>
          ))}
        </div>
      ) : (
        <div>
          <div className="folder-header">
            <button onClick={() => setCurrentFolder('')}>Back to Folders</button>
            <h2>{currentFolder}</h2>
          </div>
          
          <div className="upload-area">
            <h3>Upload Files</h3>
            <p className="upload-info">Select a file and click the Upload button</p>
            <div className="upload-controls">
              <input 
                id="file-upload"
                type="file" 
                onChange={handleFileSelect} 
                disabled={uploading}
              />
              <button 
                className="upload-button" 
                onClick={startUpload} 
                disabled={!selectedFile || uploading}
              >
                Upload
              </button>
            </div>
            {selectedFile && (
              <div className="selected-file">
                <p>Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})</p>
              </div>
            )}
            {uploading && (
              <div className="upload-progress">
                <div 
                  className="progress-bar" 
                  style={{ 
                    width: `${uploadProgress}%`,
                    height: '20px',
                    backgroundColor: '#ff0000',
                    borderRadius: '4px',
                    marginTop: '10px'
                  }}
                ></div>
                <p>{uploadStatus || `${uploadProgress}% Uploaded`}</p>
              </div>
            )}
            {!uploading && uploadStatus && (
              <p className="upload-status">{uploadStatus}</p>
            )}
          </div>
          
          <div className="file-list">
            {files.length === 0 ? (
              <p>No files in this folder yet.</p>
            ) : (
              <ul>
                {files.map((file) => (
                  <li key={file.key} className="file-item">
                    <span className="file-name">{file.key.split('/').pop()}</span>
                    <span className="file-size">{file.size ? formatFileSize(file.size) : 'Unknown size'}</span>
                    <div className="file-actions">
                      <button onClick={() => handleDownload(file.key)}>Download</button>
                      <button onClick={() => handleDelete(file.key)}>Delete</button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      
      <div className="band-logo">
        <img src="/lm.png" alt="Band Logo" style={{ maxWidth: '300px', marginTop: '40px' }} />
      </div>
    </div>
  );
};

export default FileExplorer;
